import { FC, useCallback, useState, MouseEvent, ReactNode, useEffect } from "react";
import { useContentChannel } from "./ContentChannel";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";
import { Button, ButtonAppearance, Buttons, Icon, Panel, TabbedContainer, useSimpleTabState } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import styled from "styled-components";

const MinMaxCloseButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.LightGrey["300"],
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.withAlpha(Color.LightGrey["300"], 0.8),
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.withAlpha(Color.LightGrey["300"], 0.5),
  },
} as ButtonAppearance;

const CloseButtonAppearance = {
  ...Buttons.Functional.Tertiary.Compact,
  padding: {
    vertical: 0,
    horizontal: 0,
  },
} as ButtonAppearance;

/** The parameters passed in to task subscriptions. */
export interface TaskParams {

  /** The unique task identifier generated when the task is started. */
  readonly id: string;

  /** The task title. */
  readonly title: ReactNode;

  /** The function to call when the task is being closed. */
  readonly onClose?: () => void;
}

/** The `TaskFlyout` component properties. */
export interface TaskFlyoutProps {

  /**
   * Called when the task flyout visibility changes.
   */
  readonly onVisibilityChange?: (visible: boolean) => void;
}

const Root = styled(Stack)`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 999999999;
  width: 400px;
  height: 52px;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: height ease-in-out 200ms;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.16);
  max-height: calc(100vh - 2rem - 63px);
  max-width: calc(100vw - 2rem);
  opacity: 0.9;

  &>.header {
    background-color: ${Color.toString(Color.LightGrey[800])};
    transition: background-color linear 200ms;
    color: ${Color.toString(Color.White)};
    height: 52px;
    min-height: 52px;
    flex: 0;
    cursor: pointer;

    &>.shelf {
      height: 100%;

      &>.shelf:first-child {
        padding: 1rem 1.5rem;
        height: 100%;
      }
    }
  }

  &>.content {
    background-color: ${Color.toString(Color.White)};
    flex: 1;
    display: flex;
    overflow: hidden;
    min-height: 0;

    .task-container {
      border:none;
      box-shadow: none;
    }
    
    &.single {
      &>.task-container {
        flex: 1;
        overflow-y: auto;
      }
    }
  }

  &.expanded {
    opacity: 1;

    &.small {
      height: 320px;  
    }

    &.large {
      height: 640px;
    }

    &.mobile {
      height: 100vh;
    }

    &>.header {
      background-color: ${Color.toString(Color.LightGrey[800])};

      &:hover {
        background-color: ${Color.toString(Color.lighten(Color.LightGrey[800], 0.1))};
      }
    }
  }
`

/**
 * A component which displays a box containing a set of available tasks which can be performed.
 */
export const TaskFlyout: FC<TaskFlyoutProps> = (props) => {

  const { onVisibilityChange } = props;

  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState<"small" | "large">("small");
  const subscriptions = useContentChannel(null, "flyout-tasks");
  const [selectedTab, tabChangeHandler] = useSimpleTabState((subscriptions[0]?.params as TaskParams)?.id);

  const { actualSize, allowResize } = useResponsive({
    actualSize: { desktop: size, mobile: "mobile" },
    allowResize: { desktop: true, mobile: false },
  });

  const headerClickHandler = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  const buttonContainerClick = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const resizeClickHandler = useCallback(() => {
    setSize((current) => {
      if (current === "small") return "large";
      return "small";
    });
  }, []);

  const singleTaskCloseFunction = (subscriptions.length === 1) ? (subscriptions[0].params as TaskParams | undefined)?.onClose : null;

  const closeClickHandler = useCallback(() => {
    singleTaskCloseFunction?.();
  }, [singleTaskCloseFunction]);

  useEffect(() => {
    if (subscriptions.length === 0) {
      onVisibilityChange?.(false);
    } else {
      onVisibilityChange?.(true);
    }
  }, [subscriptions.length, onVisibilityChange]);

  if (subscriptions.length === 0)
    return null;

  let title: ReactNode;
  let contentArea: ReactNode;

  if (subscriptions.length > 1) {
    title = "Tasks";
    contentArea = (
      <TabbedContainer className="content multi" selectedTab={selectedTab} onSelectedTabChange={tabChangeHandler}>
        {subscriptions.map((subscription) => {
          const { component: Component, params } = subscription;
          const taskParams = params as TaskParams | undefined;
          if (!taskParams) return false;

          return (
            <TabbedContainer.TabPane
              className="task-container"
              key={taskParams.id}
              label={(
                <Shelf spacing="small">
                  <div>{taskParams.title}</div>
                  {(taskParams.onClose) && (<Button icon="x" appearance={CloseButtonAppearance} onClick={taskParams.onClose} />)}
                </Shelf>
              )}
              id={taskParams.id} >
              <Component />
            </TabbedContainer.TabPane>
          );
        })}
      </TabbedContainer>
    );
  } else {
    const { component: Component, params } = subscriptions[0];
    const taskParams = params as TaskParams | undefined;
    if (!taskParams) {
      title = "Invalid Task";
      contentArea = <div className="content single" />;
    } else {
      title = taskParams.title;
      contentArea = (
        <div className="content single">
          <Panel className="task-container">
            <Component />
          </Panel>
        </div>
      );
    }
  }

  return (
    <Root className={classNames(actualSize, { expanded })} spacing="none">
      <div className="header" onClick={headerClickHandler}>
        <Shelf alignment="top">
          <Shelf spacing="small">
            <Icon name={(expanded) ? "chevron-down" : "chevron-up"} size="regular" color={Color.LightGrey[300]} />
            <div>{title}</div>
            <Shelf.Spacer />
          </Shelf>
          <Shelf.Spacer />
          <Shelf alignment="top" spacing="none">
            {(expanded && allowResize) && (
              <div onClick={buttonContainerClick} onMouseDown={buttonContainerClick}>
                <Button
                  appearance={MinMaxCloseButtonAppearance}
                  icon={(size === "large") ? "arrow-down-right" : "arrow-up-right"}
                  onClick={resizeClickHandler}
                  tooltip={(size === "large") ? "Minimize" : "Maximize"}
                />
              </div>
            )}
            {(subscriptions.length === 1 && singleTaskCloseFunction) && (
              <div onClick={buttonContainerClick} onMouseDown={buttonContainerClick}>
                <Button
                  appearance={MinMaxCloseButtonAppearance}
                  icon="x"
                  onClick={closeClickHandler}
                  tooltip="Close"
                />
              </div>
            )}
          </Shelf>
        </Shelf>
      </div>
      {contentArea}
    </Root>
  );
};
